import React from "react";
import { useState, useEffect } from "react";
import EventDisplay from "../components/Cards/EventCard/EventDisplay";
import Header from "../components/Cards/CardComponents/Header";
import FAQSection from "../components/Common/FAQ";
import Testimonials from "../components/Common/Testimonials";
import SubscribeLandingpage from "../components/subscribe_landingpage";
import {
  ALLPUJA,
  ALLOFFERINGS,
  PUJAFAQ,
  OFFERINGFAQ,
  TESTIMONIAL,
} from "../rest/endpoints";
import ConditionalRenderer from "../devsathan_components/ConditionalRenderer";
import { useAuthHeaders } from "../utils/getHeaders";
import { apiRequest } from "../utils/apiRequest";
import { toast } from "react-toastify";
import LoadingIndicator from "../components/Common/Loading";
import FirstPageOutlinedIcon from "@mui/icons-material/FirstPageOutlined";
import LastPageOutlinedIcon from "@mui/icons-material/LastPageOutlined";
import { useParams } from "react-router-dom";

const EventListingPage = () => {
  const { eventtype } = useParams();

  const headers = useAuthHeaders();
  const [testimonials, setTestimonials] = useState([]);
  const [event, setEvent] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Function to get visible page numbers (max 5 at once)
  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    const pageNumbers = [];
    
    if (totalPages <= maxVisible) {
      // If total pages is 5 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always include page 1
      pageNumbers.push(1);
      
      if (currentPage <= 3) {
        // Show first 4 pages + last page: 1, 2, 3, 4, last
        for (let i = 2; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show first page + last 4 pages: 1, ..., last-3, last-2, last-1, last
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Show: 1, ..., current-1, current, current+1, ..., last
        pageNumbers.push('...');
        pageNumbers.push(currentPage - 1);
        pageNumbers.push(currentPage);
        pageNumbers.push(currentPage + 1);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const fetchPooja = async () => {
      const url = eventtype === "puja" ? ALLPUJA : ALLOFFERINGS; // Adjust the URL based on event type
      try {
        const result = await apiRequest({
          url: `${url}${currentPage - 1}`,
          method: "GET",
          headers,
        });

        eventtype === "puja"
          ? setEvent(result?.data?.pujaList)
          : setEvent(result?.data?.offeringList);

        setTotalPages(result?.data?.totalPages);
      } catch (error) {
        console.error(
          "Error fetching puja events:",
          error.message || "Something went wrong"
        );
        toast.error("Error fetching puja events. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPooja();
  }, [eventtype, currentPage]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const result = await apiRequest({
          url: TESTIMONIAL,
          method: "GET",
          headers,
        });

        // Check if testimonial data is an array and update state
        if (result?.data?.reviews && Array.isArray(result.data.reviews)) {
          setTestimonials(result.data.reviews);
        } else {
          console.error("Testimonial data is not available or not an array");
          toast.error("Testimonial data is not available.");
        }
      } catch (error) {
        // Handle errors
        console.error(
          "Error fetching testimonials:",
          error.message || "Something went wrong"
        );
        toast.error("Error fetching testimonials. Please try again.");
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    const url = eventtype === "puja" ? PUJAFAQ : OFFERINGFAQ; // Adjust the URL based on event type
    const fetchfaq = async () => {
      try {
        const result = await apiRequest({
          url: url,
          method: "GET",
          headers,
        });

        // Check if FAQ data is an array and update state
        if (result?.data?.faqs && Array.isArray(result.data.faqs)) {
          setFaqs(result.data.faqs);
        } else {
          console.error("FAQ data is not available or not an array");
          toast.error("FAQ data is not available.");
        }
      } catch (error) {
        // Handle errors
        console.error(
          "Error fetching FAQs:",
          error.message || "Something went wrong"
        );
        toast.error("Error fetching FAQs. Please try again.");
      }
    };

    fetchfaq();
  }, [eventtype]);

  if (eventtype !== "puja" && eventtype !== "chadawa") {
    return;
  }

  return (
    <div
      key={eventtype || currentPage}
      className="bg-[#FFECE5] min-h-screen  pt-1 -mt-10 md:-mt-16"
    >
      <div className=" max-w-full mx-auto px-6 sm:px-16  py-10 md:py-20">
        <div>
          <Header eventtype={eventtype} />
        </div>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <div>
            <div className="overflow-x-auto whitespace-nowrap  sm:-mt-20">
              <EventDisplay
                events={event}
                eventtype={eventtype}
                viewType="list"
              />
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center mt-4 items-center">
                {/* Previous button */}
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`mx-1 px-2 py-2 rounded-md ${
                    currentPage === 1 
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                      : "bg-dark-pink hover:bg-custom-orange hover:text-white"
                  }`}
                >
                  <FirstPageOutlinedIcon />
                </button>
                
                {getVisiblePageNumbers().map((item, index) => (
                  item === '...' ? (
                    <span key={`ellipsis-${index}`} className="mx-1 px-2 py-2 text-gray-600 text-sm">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => handlePageChange(item)}
                      className={`mx-1 px-3 py-2 rounded-md ${
                        currentPage === item
                          ? "bg-custom-orange text-white"
                          : "bg-dark-pink hover:bg-custom-orange hover:text-white"
                      }`}
                    >
                      {item}
                    </button>
                  )
                ))}
                
                {/* Next button */}
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`mx-1 px-2 py-2 rounded-md ${
                    currentPage === totalPages 
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                      : "bg-dark-pink hover:bg-custom-orange hover:text-white"
                  }`}
                >
                 <LastPageOutlinedIcon />
                </button>
              </div>
            )}
          </div>
        )}
        <div>
          <ConditionalRenderer componentKey="SubscribeLandingpage">
            <SubscribeLandingpage />
          </ConditionalRenderer>
        </div>
        <div className="md:px-14 -mb-10 sm:-mt-10  ">
          <Testimonials testimonials={testimonials} />
        </div>

        <div className="mt-8 -mx-4 sm:mt-2 sm:-mb-10">
          <FAQSection faqs={faqs} />
        </div>
      </div>
    </div>
  );
};

export default EventListingPage;
